import { RequiredActions } from '@/api/sign-in';
import {
  AdditionalValidations,
  AgreementResponse,
  CredentialType,
  IdentifierType,
  ValidatedAgreements,
} from '@/api/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type SignInSession = {
  transactionId: string;
  redirectUrl: string;
  passwordAttemptsRemaining: number;
  requiredActions: RequiredActions[];
  identifier: string;
  identifierType?: IdentifierType;
  validatedAgreements: ValidatedAgreements[];
  additionalValidations?: AdditionalValidations;
  email?: string;
  lexisNexisSessionId?: string;
  isVerified: boolean;
  exhaustedMethods: CredentialType[];
};

const defaultSession: SignInSession = {
  identifier: '',
  transactionId: '',
  redirectUrl: '',
  passwordAttemptsRemaining: 6,
  requiredActions: [],
  validatedAgreements: [],
  isVerified: false,
  exhaustedMethods: [],
};

export type PartialSession =
  | Partial<SignInSession>
  | ((s: SignInSession) => SignInSession);

export interface SignInStore {
  session: SignInSession;
  updateSession: (session: PartialSession) => void;
  updateVerificationResult: (
    result: AgreementResponse,
    mergeAgreements?: boolean,
  ) => void;
  clearIdentifier: () => void;
  addExhaustedMethods: (...types: CredentialType[]) => void;
}

export const useSignInStore = create(
  persist<SignInStore>(
    (set) => ({
      session: defaultSession,
      updateSession: (session: PartialSession) => {
        set(
          (s) => ({
            session:
              typeof session === 'function'
                ? session(s.session)
                : { ...s.session, ...session },
          }),
          false,
        );
      },
      updateVerificationResult: (
        result: AgreementResponse,
        mergeAgreements?: boolean,
      ) =>
        set((s) => {
          const validatedAgreements = result.validatedAgreements ?? [];
          return {
            session: {
              ...s.session,
              validatedAgreements: mergeAgreements
                ? s.session.validatedAgreements.concat(validatedAgreements)
                : validatedAgreements,
              additionalValidations: result.additionalValidations,
              isVerified: true,
            },
          };
        }),
      clearIdentifier: () => {
        set((s) => ({
          session: {
            ...s.session,
            identifier: '',
            identifierType: undefined,
            additionalValidations: undefined,
            validatedAgreements: [],
            transactionId: '',
            exhaustedMethods: [],
          },
        }));
      },
      addExhaustedMethods: (...types) =>
        set((s) => ({
          session: {
            ...s.session,
            exhaustedMethods: s.session.exhaustedMethods.concat(types),
          },
        })),
    }),
    {
      name: 'sign-in-store',
      storage: createJSONStorage(() => sessionStorage),
      //only hydrate when rendering to avoid hydration errors
      skipHydration: true,
    },
  ),
);
